import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera } from 'lucide-react';

interface EditProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  onSave: (newName: string) => Promise<void>;
  onAvatarClick: () => void;
}

export const EditProfileDialog = ({
  isOpen,
  onClose,
  currentName,
  onSave,
  onAvatarClick,
}: EditProfileDialogProps) => {
  const [name, setName] = useState(currentName);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
    }
  }, [isOpen, currentName]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setIsSaving(true);
    try {
      await onSave(name.trim());
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md w-[90%] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading font-bold">Editar Perfil</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="flex flex-col items-center justify-center gap-2">
            <Button 
              variant="outline" 
              onClick={onAvatarClick}
              className="rounded-full h-12 px-6 bg-primary/5 text-primary border-primary/20 hover:bg-primary/10"
            >
              <Camera className="h-4 w-4 mr-2" />
              Cambiar foto de perfil
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              O toca tu foto en la pantalla principal para cambiarla.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nombre</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre"
              className="h-12 rounded-xl"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving} className="w-full sm:w-auto h-12 rounded-xl">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !name.trim() || name === currentName} className="w-full sm:w-auto h-12 rounded-xl">
            {isSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
